import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { refreshToken } from '../api';

// Create AuthContext
export const AuthContext = createContext();

// Helper function to check if token is expired
const isTokenExpired = (decodedToken) => {
  console.log('isTokenExpired called with decodedToken:', decodedToken);
  if (!decodedToken.exp) {
    return false; // No expiration claim, assume valid
  }
  // exp is in seconds, Date.now() is in milliseconds
  const currentTime = Math.floor(Date.now() / 1000);
  return decodedToken.exp < currentTime;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        // Check if token is expired
        if (isTokenExpired(decodedToken)) {
          console.log('Token is expired, removing from localStorage');
          localStorage.removeItem('token');
          return null;
        }
        return {
          token,
          ...decodedToken
        };
      } catch (error) {
        localStorage.removeItem('token');
        return null;
      }
    }
    return null;
  });

  // Track if we're checking/refreshing token to prevent race conditions
  const [isCheckingAuth, setIsCheckingAuth] = useState(() => {
    // If we have a token but no session flag, we need to check
    const token = localStorage.getItem('token');
    const hasSessionFlag = sessionStorage.getItem('hasActiveSession');
    const needsCheck = !!(token && !hasSessionFlag);
    
    // If we need to check, verify if we can decode the token (meaning we have a valid user)
    // If token is valid and decodable, don't block - refresh can happen in background
    if (needsCheck && token) {
      try {
        const decodedToken = jwtDecode(token);
        // If token is not expired, we have a valid user, so don't block
        if (!isTokenExpired(decodedToken)) {
          return false; // Don't block - user exists from token
        }
      } catch (error) {
        // Token can't be decoded, we'll need to block
      }
    }
    
    // Only block if we need to check AND don't have a valid token/user
    return needsCheck;
  });

  // Check if we need to refresh token on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const hasSessionFlag = sessionStorage.getItem('hasActiveSession');
    
    console.log('AuthContext init - token exists:', !!token, 'session flag exists:', !!hasSessionFlag, 'user exists:', !!user);
    
    if (token && !hasSessionFlag) {
      // Token exists but no session flag - this is a bookmark/new session
      // If we already have a user from localStorage, don't block - refresh in background
      // Only set isCheckingAuth if we don't have a user (shouldn't happen, but safety check)
      const hasUserFromInit = !!user;
      if (!hasUserFromInit) {
        setIsCheckingAuth(true);
      }
      console.log('Refreshing token for bookmark/new session (non-blocking:', hasUserFromInit, ')');
      // Start refreshToken but don't block - update user when it completes
      refreshToken({ updateLastLogin: true }).then(newToken => {
        const decodedToken = jwtDecode(newToken);
        setUser({
          token: newToken,
          ...decodedToken
        });
        sessionStorage.setItem('hasActiveSession', 'true');
        console.log('Token refreshed and session flag set');
      }).catch(error => {
        console.error('Token refresh failed:', error);
        localStorage.removeItem('token');
        setUser(null);
        // Don't redirect here - let the routing handle it
      }).finally(() => {
        setIsCheckingAuth(false);
      });
    } else {
      // No token refresh needed, we're done checking
      setIsCheckingAuth(false);
    }
  }, []); // Keep empty deps - user is from initial state, not a dependency

  useEffect(() => {
    if (user?.token) {
      localStorage.setItem('token', user.token);
      // Ensure session flag is set when user is logged in
      if (!sessionStorage.getItem('hasActiveSession')) {
        sessionStorage.setItem('hasActiveSession', 'true');
        console.log('Session flag set from user state change');
      }
    } else {
      localStorage.removeItem('token');
    }
  }, [user]);

  const isLoggedIn = () => !!user?.token;
  const hasPermission = (permissionPath) => {
    if (!user?.permissions) return false;
    
    const pathParts = permissionPath.split('.');
    let current = user.permissions;
    
    for (const part of pathParts) {
      if (current[part] === undefined) return false;
      current = current[part];
    }
    
    return current === true;
  };

  const loginUser = (token) => {
    console.log('loginUser called with token:', !!token);
    try {
      const decodedToken = jwtDecode(token);
      setUser({
        token,
        ...decodedToken
      });
      // Always set session flag when loginUser is called (new login)
      sessionStorage.setItem('hasActiveSession', 'true');
      console.log('Login successful - session flag set');
    } catch (error) {
      console.error('Invalid token:', error);
      setUser(null);
      sessionStorage.removeItem('hasActiveSession');
    }
  };

  const logoutUser = () => {
    setUser(null);
    sessionStorage.removeItem('hasActiveSession');
  };

  const registerUser = (userData) => {
    console.log('Registering user:', userData);
    // Stubbed function: In a real app, you would handle registration logic here
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      isLoggedIn, 
      hasPermission,
      loginUser, 
      logoutUser,
      registerUser,
      isCheckingAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};

// Export AuthProvider as default
export default AuthProvider;
